import React, { useMemo, useState, useRef } from 'react';
import { toast } from 'react-toastify';
import { BookOpen, PlayCircle, Headphones, X, LifeBuoy } from 'lucide-react';
import ApiServices from '../../shared/ApiServices/ApiServices';
import { V2Button, V2Card, V2Modal, V2Input } from '../../shared/custom-ui/v2-components';
import ModernPageHeader from '../WorkListTable/components/ModernPageHeader';

const Icon = { BookOpen, PlayCircle, Headphones, X } as any;

interface FormData {
    issueType: string;
    description: string;
}

const DESC_MAX = 1000;

/* ─── Resource Card (v2 tokens) ──────────────────────────────── */
const V2ResourceCard: React.FC<{
    icon: React.ReactNode;
    title: string;
    description: string;
    onClick?: () => void;
    link?: string;
    external?: boolean;
}> = ({ icon, title, description, onClick, link, external }) => {
    const cls = `flex flex-col p-6 border border-[var(--v2-border-card)] rounded-xl bg-[var(--v2-bg-card)] shadow-[var(--v2-shadow-card)] overflow-hidden transition-all duration-200 hover:shadow-[var(--v2-shadow-card-hover)] hover:border-[var(--v2-border-glass-strong)] ${onClick || link ? 'cursor-pointer' : ''}`;

    const content = (
        <>
            <div className="w-14 h-14 rounded-xl bg-[var(--v2-primary-50)] text-[var(--v2-primary-500)] flex items-center justify-center mb-4 transition-transform duration-300">
                {icon}
            </div>
            <h3 className="text-lg font-bold text-[var(--v2-text-heading)] mb-2">{title}</h3>
            <p className="text-[15px] leading-relaxed text-[var(--v2-text-secondary)]">{description}</p>
        </>
    );

    if (link) {
        return (
            <a
                href={link}
                target={external ? '_blank' : undefined}
                rel={external ? 'noopener noreferrer' : undefined}
                className={`${cls} no-underline`}
                onClick={onClick}
            >
                {content}
            </a>
        );
    }

    return (
        <div
            role={onClick ? 'button' : undefined}
            tabIndex={onClick ? 0 : undefined}
            aria-label={title}
            className={cls}
            onClick={onClick}
            onKeyDown={(e) => { if (onClick && (e.key === 'Enter' || e.key === ' ')) onClick(); }}
        >
            {content}
        </div>
    );
};

/* ─── Main ───────────────────────────────────────────────────── */
const SupportForm: React.FC = () => {
    const [formData, setFormData] = useState<FormData>({ issueType: '', description: '' });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errors, setErrors] = useState<{ [key: string]: string }>({});

    // Video modal state
    const [showVideoModal, setShowVideoModal] = useState(false);
    const videoRef = useRef<HTMLVideoElement>(null);

    const demoVideoUrl = process.env.DOCUMENT_VIDEO || 'https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/BigBuckBunny.mp4';
    const documentPdf = process.env.DOCUMENT_PDF;

    const issueOptions = useMemo(
        () => [
            { label: 'Billing', value: 'billing' },
            { label: 'A bug', value: 'bug' },
            { label: 'Other', value: 'other' },
        ],
        []
    );

    const validateForm = () => {
        const newErrors: { [key: string]: string } = {};
        if (!formData.issueType) newErrors.issueType = 'Please select an issue type.';
        if (!formData.description) newErrors.description = 'Please provide a description.';
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { id, value } = e.target;
        setFormData((prev) => ({ ...prev, [id]: value }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!validateForm()) return;

        setIsSubmitting(true);
        try {
            const response = await ApiServices.createSupportTicket(formData.issueType, formData.description);
            toast.success(response?.message);
            setFormData({ issueType: '', description: '' });
            setErrors({});
        } catch (error: any) {
            toast.error(error?.response?.data?.message || 'Something went wrong');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDocumentationClick = () => {
        if (documentPdf) window.open(documentPdf, '_blank', 'noopener,noreferrer');
    };

    const handleVideoClick = () => {
        if (demoVideoUrl) setShowVideoModal(true);
    };

    const handleCloseVideoModal = () => {
        setShowVideoModal(false);
        if (videoRef.current) videoRef.current.pause();
    };

    const selectedIssueLabel =
        formData.issueType
            ? issueOptions.find((o) => o.value === formData.issueType)?.label || 'Select Issue Type'
            : 'Select Issue Type';

    const descCount = formData.description.length;
    const descOver = descCount > DESC_MAX;

    return (
        <div className="relative">
            {/* ══════ HEADER ══════ */}
            <ModernPageHeader
                title="Support"
                subtitle="Get help, submit a ticket, or access learning resources"
                icon={<LifeBuoy className="w-[15px] h-[15px]" />}
            />

            {/* ══════ FORM CARD ══════ */}
            <V2Card className="mb-6">
                <div className="text-center mb-6">
                    <h2 className="text-[22px] font-semibold text-[var(--v2-text-heading)]">
                        Submit a Support Ticket
                    </h2>
                    <p className="mt-1 text-[15px] text-[var(--v2-text-secondary)]">
                        Please provide details of your issue so we can assist you.
                    </p>
                </div>

                <form onSubmit={handleSubmit} className="flex flex-col gap-5">
                    {/* Issue Type — native select styled with v2 tokens */}
                    <div>
                        <label
                            htmlFor="issueType"
                            className="block text-[15px] font-semibold text-[var(--v2-text-primary)] mb-1.5"
                        >
                            Issue Type
                        </label>
                        <select
                            id="issueType"
                            value={formData.issueType}
                            onChange={handleChange}
                            className={`w-full text-[15px] px-3.5 py-2.5 rounded-lg border bg-[var(--v2-bg-card)] outline-none cursor-pointer transition-colors ${errors.issueType ? 'border-[var(--v2-danger)]' : 'border-[var(--v2-border-glass-strong)]'} ${formData.issueType ? 'text-[var(--v2-text-primary)]' : 'text-[var(--v2-text-muted)]'}`}
                        >
                            <option value="">{selectedIssueLabel === 'Select Issue Type' ? 'Select Issue Type' : selectedIssueLabel}</option>
                            {issueOptions.map((o) => (
                                <option key={o.value} value={o.value}>{o.label}</option>
                            ))}
                        </select>
                        {errors.issueType && (
                            <p className="mt-1 text-sm font-medium text-[var(--v2-danger)]">
                                {errors.issueType}
                            </p>
                        )}
                    </div>

                    {/* Description */}
                    <div>
                        <div className="flex items-end justify-between">
                            <label
                                htmlFor="description"
                                className="block text-[15px] font-semibold text-[var(--v2-text-primary)] mb-1.5"
                            >
                                Description
                            </label>
                            <span
                                className={`text-[13px] font-medium ${descOver ? 'text-[var(--v2-danger)]' : 'text-[var(--v2-text-muted)]'}`}
                                aria-live="polite"
                            >
                                {descCount}/{DESC_MAX}
                            </span>
                        </div>
                        <textarea
                            id="description"
                            value={formData.description}
                            onChange={handleChange}
                            placeholder="Enter a detailed description of your issue"
                            maxLength={DESC_MAX + 500}
                            className={`w-full h-36 resize-none text-[15px] p-3 rounded-xl border bg-[var(--v2-bg-surface)] text-[var(--v2-text-primary)] outline-none shadow-[inset_0_1px_2px_rgba(0,0,0,0.04)] transition-colors placeholder:text-[var(--v2-text-muted)] ${descOver || errors.description ? 'border-[var(--v2-danger)]' : 'border-[var(--v2-border-glass-strong)]'}`}
                            aria-invalid={!!errors.description || descOver}
                        />
                        {errors.description && (
                            <p className="mt-1 text-sm font-medium text-[var(--v2-danger)]">
                                {errors.description}
                            </p>
                        )}
                    </div>

                    {/* Submit */}
                    <div className="flex justify-center pt-2">
                        <V2Button
                            type="submit"
                            variant="primary"
                            size="small"
                            loading={isSubmitting}
                            disabled={isSubmitting}
                            style={{ minWidth: 140 }}
                            aria-label={isSubmitting ? 'Submitting' : 'Submit'}
                        >
                            Submit
                        </V2Button>
                    </div>
                </form>
            </V2Card>

            {/* ══════ HELP RESOURCES ══════ */}
            <div className="grid gap-5" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))' }}>
                <V2ResourceCard
                    icon={<Icon.BookOpen style={{ width: 24, height: 24 }} />}
                    title="Documentation"
                    description="Browse user guides and tutorials to learn about platform features"
                    onClick={handleDocumentationClick}
                />
                <V2ResourceCard
                    icon={<Icon.PlayCircle style={{ width: 24, height: 24 }} />}
                    title="Video"
                    description="Watch step-by-step instructions and platform demonstrations"
                    onClick={demoVideoUrl ? handleVideoClick : undefined}
                />
                <V2ResourceCard
                    icon={<Icon.Headphones style={{ width: 24, height: 24 }} />}
                    title="Contact Support"
                    description="Reach our support team directly for personalized assistance"
                    link="mailto:support@symbianhealth.com"
                    external
                />
            </div>

            {/* ══════ VIDEO MODAL ══════ */}
            <V2Modal
                isOpen={showVideoModal}
                onClose={handleCloseVideoModal}
                size="xl"
                title="Platform Demo Video"
                showCloseButton
                footer={
                    <p style={{ fontSize: 13, textAlign: 'center', color: 'var(--v2-text-muted)' }}>
                        Watch this comprehensive demo to learn how to use the Symbian RAD Platform effectively.
                    </p>
                }
            >
                <div style={{ background: '#000', borderRadius: 'var(--v2-radius-md)', overflow: 'hidden' }}>
                    <video
                        ref={videoRef}
                        style={{ width: '100%', height: 'auto', display: 'block' }}
                        controls
                        autoPlay
                        crossOrigin="anonymous"
                    >
                        <source src={demoVideoUrl} type="video/mp4" />
                        Your browser does not support the video tag.
                    </video>
                </div>
            </V2Modal>
        </div>
    );
};

export default SupportForm;
